'use client';

import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Card, CardContent } from '../components/ui/Card';
import { FileXls, Lock, ArrowRight, CheckCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { authApi } from '../services/api';
import { Footer } from './components/Footer';

// Magnetic Button Component
function MagneticButton({ children, ...props }: React.ComponentProps<typeof Button>) {
  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
    >
      <Button {...props}>{children}</Button>
    </motion.div>
  );
}

export function ResetPasswordPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isValidating, setIsValidating] = useState(true);
  const [isTokenValid, setIsTokenValid] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  // 验证 token
  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setIsValidating(false);
        return;
      }

      try {
        const response = await authApi.validateResetToken(token);
        if (response.code === 0 && response.data?.valid) {
          setIsTokenValid(true);
        } else {
          setError(t('auth.invalidResetLink'));
        }
      } catch (err) {
        setError(t('auth.invalidResetLink'));
      } finally {
        setIsValidating(false);
      }
    };

    validateToken();
  }, [token, t]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const validateForm = () => {
    if (formData.password.length < 6) {
      setError(t('auth.passwordTooShort'));
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return false;
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) return;
    if (!token) {
      setError(t('auth.invalidResetLink'));
      return;
    }

    setIsLoading(true);

    try {
      const response = await authApi.resetPassword(token, formData.password);

      if (response.code !== 0) {
        throw new Error(response.message || t('auth.resetFailed'));
      }

      setIsSuccess(true);
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : t('auth.resetFailed');
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // Stagger animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring' as const,
        stiffness: 100,
        damping: 20,
      },
    },
  };

  // 加载中状态
  if (isValidating) {
    return (
      <div className="min-h-[100dvh] bg-zinc-50 flex items-center justify-center">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center"
        >
          <div className="w-12 h-12 border-4 border-zinc-200 border-t-emerald-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-zinc-500">{t('auth.validatingLink')}</p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-[100dvh] bg-zinc-50 flex">
      {/* Left Side - Branding (Asymmetric Layout) */}
      <motion.div 
        className="hidden lg:flex lg:w-1/2 bg-zinc-900 relative overflow-hidden"
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
      >
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-30">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.15) 1px, transparent 0)`,
            backgroundSize: '32px 32px',
          }} />
        </div>
        
        {/* Floating Elements */}
        <motion.div
          className="absolute top-1/4 left-1/4 w-64 h-64 bg-emerald-500/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />
        <motion.div
          className="absolute bottom-1/3 right-1/4 w-96 h-96 bg-zinc-700/30 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        />

        {/* Content */}
        <div className="relative z-10 flex flex-col justify-center px-16 xl:px-24">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          >
            <div className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/10 mb-8">
              <FileXls className="w-5 h-5 text-emerald-400" weight="fill" />
              <span className="text-sm font-medium text-white/90">{t('app.name')}</span>
            </div>
            
            <h1 className="text-5xl xl:text-6xl font-bold text-white tracking-tight leading-[1.1] mb-6">
              {t('auth.resetPasswordTitle')}
              <br />
              <span className="text-emerald-400">{t('auth.resetPasswordSubtitle')}</span>
            </h1>
            
            <p className="text-lg text-zinc-400 max-w-md leading-relaxed">
              {t('auth.enterNewPassword')}
            </p>
          </motion.div>
        </div>
      </motion.div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-6 lg:p-12">
        <motion.div
          className="w-full max-w-md"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Mobile Logo */}
          <motion.div variants={itemVariants} className="lg:hidden text-center mb-8">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-zinc-900 mb-4 overflow-hidden">
              <img src="/logo.svg" alt="Logo" className="w-8 h-8" />
            </div>
            <h1 className="text-2xl font-bold text-zinc-900">{t('app.name')}</h1>
          </motion.div>

          <AnimatePresence mode="wait">
            {!isTokenValid ? (
              <motion.div
                key="invalid"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <Card variant="elevated" className="overflow-hidden">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center"
                    >
                      <XCircle className="w-10 h-10 text-red-600" weight="fill" />
                    </motion.div>

                    <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight mb-2">
                      {t('auth.linkExpired')}
                    </h2>
                    <p className="text-zinc-500 mb-6">
                      {error || t('auth.invalidResetLink')}
                    </p>

                    <Link to="/forgot-password">
                      <MagneticButton
                        variant="primary"
                        size="lg"
                        className="w-full"
                      >
                        {t('auth.requestNewReset')}
                      </MagneticButton>
                    </Link>
                  </CardContent>
                </Card>
              </motion.div>
            ) : !isSuccess ? (
              <motion.div variants={itemVariants} key="form">
                <Card variant="elevated" className="overflow-hidden">
                  <CardContent className="p-8">
                    <div className="mb-8">
                      <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight">
                        {t('auth.resetPassword')}
                      </h2>
                      <p className="text-zinc-500 mt-2 text-sm">
                        {t('auth.enterNewPassword')}
                      </p>
                    </div>

                    {error && (
                      <motion.div
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="mb-6 p-4 bg-red-50 border border-red-100 rounded-xl text-sm text-red-600 flex items-start gap-2"
                      >
                        <WarningCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                        <span>{error}</span>
                      </motion.div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <Input
                        label={t('auth.newPassword')}
                        name="password"
                        type="password"
                        value={formData.password}
                        onChange={handleChange}
                        leftIcon={<Lock className="w-5 h-5" />}
                        placeholder={t('auth.placeholderNewPassword')}
                        required
                      />

                      <Input
                        label={t('auth.confirmNewPassword')}
                        name="confirmPassword"
                        type="password"
                        value={formData.confirmPassword}
                        onChange={handleChange}
                        leftIcon={<Lock className="w-5 h-5" />}
                        placeholder={t('auth.placeholderConfirmPassword')}
                        required
                      />

                      <div className="pt-2">
                        <MagneticButton
                          type="submit"
                          variant="primary"
                          size="lg"
                          isLoading={isLoading}
                          className="w-full"
                          rightIcon={<ArrowRight className="w-5 h-5" />}
                        >
                          {isLoading ? t('common.resetting') : t('auth.confirmReset')}
                        </MagneticButton>
                      </div>
                    </form>
                  </CardContent>
                </Card>
              </motion.div>
            ) : (
              <motion.div
                key="success"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.3 }}
              >
                <Card variant="elevated" className="overflow-hidden">
                  <CardContent className="p-8 text-center">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                      className="w-20 h-20 mx-auto mb-6 rounded-full bg-emerald-100 flex items-center justify-center"
                    >
                      <CheckCircle className="w-10 h-10 text-emerald-600" weight="fill" />
                    </motion.div>

                    <h2 className="text-2xl font-semibold text-zinc-900 tracking-tight mb-2">
                      {t('auth.resetSuccess')}
                    </h2>
                    <p className="text-zinc-500 mb-6">
                      {t('auth.resetSuccessDesc')}
                    </p>

                    <MagneticButton
                      variant="primary"
                      size="lg"
                      className="w-full"
                      onClick={() => navigate('/login')}
                    >
                      {t('auth.goToLogin')}
                    </MagneticButton>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Footer */}
          <Footer />
        </motion.div>
      </div>
    </div>
  );
}
